import { HeadingH2 } from '@dreamstack/feature-components/src/layout/Texts'
import { useTranslation } from '@dreamstack/i18n'
import { Trans } from 'next-i18next'
import Image from 'next/image'
import type { FunctionComponent } from 'react'
import styles from './IS24AdvantagesStrengths.module.css'

const SemiBold = (props: React.PropsWithChildren<{}>) => (
  <span className="font-semibold" {...props} />
)
const StyledLogo: FunctionComponent<React.PropsWithChildren<{}>> = ({
  children,
}) => <div className="relative self-end flex-shrink-0">{children}</div>
const ShyTag = () => {
  return <span dangerouslySetInnerHTML={{ __html: `&shy;` }}></span>
}
const NDash = () => {
  return <span dangerouslySetInnerHTML={{ __html: `&ndash;` }}></span>
}

const HeaderBox: FunctionComponent<React.PropsWithChildren<{}>> = ({
  children,
}) => (
  <div
    className={
      'flex justify-center items-center mb-px-40 md:mb-px-64 ml:mb-0 ml:h-[250px] ' +
      styles['ga-header']
    }
  >
    {children}
  </div>
)

type LogoData = {
  src: string
  alt: string
  className: string
}

const accentroLogoData: LogoData = {
  src: '/static/images/accentro-logo-w.svg',
  alt: 'Accentro Logo',
  className:
    'relative w-[67px] h-[7px] md:w-[134px] md:h-[14px] flex-shrink-0 self-end',
}

const is24LogoData: LogoData = {
  src: '/static/images/immo-scout-24-logo-2020.svg',
  alt: 'Immoscout24 Logo',
  className:
    'relative w-[60px] h-[12px] md:w-[120px] md:h-[24px] flex-shrink-0 self-end',
}

const Logo: FunctionComponent<React.PropsWithChildren<LogoData>> = ({
  src,
  alt,
  className,
}) => (
  <div className={className}>
    <Image className="object-contain" src={src} fill alt={alt} />
  </div>
)

export const IS24AdvantagesStrengths: FunctionComponent<
  React.PropsWithChildren<{}>
> = () => {
  const t = useTranslation()
  const semibold = <SemiBold />
  const shy = <ShyTag />
  const br = <br />
  const ndash = <NDash />

  const strengthsData = [
    {
      title: t('accentro:is24.numberOneOwnership'),
      text: (
        <Trans
          i18nKey="accentro:is24.17500SoldUnits"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      logo: accentroLogoData,
      area: 'ga-box01',
    },
    {
      title: t('accentro:is24.numberOneMarketplace'),
      text: (
        <Trans
          i18nKey="accentro:is24.over20MUser"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      logo: is24LogoData,
      area: 'ga-box02',
    },
    {
      title: t('accentro:is24.powerfulDistribution'),
      text: (
        <Trans
          i18nKey="accentro:is24.over2BillionVolume"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      logo: accentroLogoData,
      area: 'ga-box03',
    },
    {
      isHeaderBox: true,
      title: t('accentro:is24.ourStrengths'),
    },
    {
      title: t('accentro:is24.veryHighPurchaseDemand'),
      text: (
        <Trans
          i18nKey="accentro:is24.578Inquiries"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      smallText: t('accentro:is24.performanceEvaluation'),
      logo: is24LogoData,
      area: 'ga-box06',
    },
    {
      title: (
        <Trans
          i18nKey="accentro:is24.reliabilityAndRiskFree"
          components={{ semibold: semibold, shy: shy }}
        ></Trans>
      ),
      text: (
        <Trans
          i18nKey="accentro:is24.over60MillionVolume"
          components={{ semibold: semibold, shy: shy }}
        ></Trans>
      ),
      logo: accentroLogoData,
      area: 'ga-box05',
    },
    {
      title: t('accentro:is24.topReach'),
      text: (
        <Trans
          i18nKey="accentro:is24.78Millions"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      logo: is24LogoData,
      area: 'ga-box04',
    },
    {
      title: t('accentro:is24.succesfulPartner'),
      text: (
        <Trans
          i18nKey="accentro:is24.13000satisfiedCustomers"
          components={{ semibold: semibold }}
        ></Trans>
      ),
      logo: accentroLogoData,
      area: 'ga-box07',
    },
    {
      title: <>{t('accentro:is24.everythingUnderOneRoof')}</>,
      text: <>{t('accentro:is24.onlineOfflineMarketing')}</>,
      logo: is24LogoData,
      area: 'ga-box08',
    },
  ]
  return (
    <>
      <HeadingH2 className="text-center">
        <span>{t('accentro:is24.yourAdvantages')}</span>
      </HeadingH2>
      <div className="flex justify-center">
        <div className="flex justify-center flex-col space-y-px-8 mt-px-40 md:flex-row md:max-w-screen-md md:space-y-0 md:space-x-px-8 md:mt-px-64 lg:max-w-none">
          <div className="bg-accentroGray-full text-accentroWhite-full min-h-[300px] text-center px-px-16 py-px-24 flex flex-1 flex-col md:px-px-24 md:py-px-32 md:h-[350px] lg:w-[500px] lg:px-px-48">
            <div className="flex flex-col flex-1 justify-start mt-px-40">
              <p className="font-semibold text-px-20 leading-px-30 md:text-px-28 md:leading-px-36">
                {t('accentro:is24.saleWithAccentro')}
              </p>
              <p className="text-px-16 leading-px-24 mt-px-12 md:mt-px-16 md:text-px-16 md:leading-px-24">
                <Trans
                  i18nKey="accentro:is24.riskFreeAndMaximumSecurity"
                  components={{ semibold: semibold, br: br }}
                ></Trans>
              </p>
            </div>
            <Logo
              alt={accentroLogoData.alt}
              className={accentroLogoData.className}
              src={accentroLogoData.src}
            />
          </div>
          <div className="bg-accentroGray-full text-accentroWhite-full min-h-[300px] text-center px-px-16 py-px-24 flex flex-1 flex-col md:px-px-24 md:py-px-32 md:h-[350px] lg:w-[500px] lg:px-px-48">
            <div className="flex flex-col flex-1 justify-start mt-px-40">
              <p className="font-semibold text-px-20 leading-px-30 md:text-px-28 md:leading-px-36">
                {t('accentro:is24.marketingStrategy')}
              </p>
              <p className="text-px-16 leading-px-24 mt-px-12 md:mt-px-16 md:text-px-16 md:leading-px-24">
                <Trans
                  i18nKey="accentro:is24.marketingBest"
                  components={{ semibold: semibold, br: br, ndash: ndash }}
                ></Trans>
              </p>
            </div>
            <Logo
              alt={is24LogoData.alt}
              className={is24LogoData.className}
              src={is24LogoData.src}
            />
          </div>
        </div>
      </div>
      <div className="flex justify-center mt-px-20 ml:mt-px-32">
        <div className={styles.is24Grid}>
          {strengthsData.map((s, i) => {
            if (s.isHeaderBox)
              return (
                <HeaderBox key={i}>
                  <HeadingH2>
                    <span>{s.title}</span>
                  </HeadingH2>
                </HeaderBox>
              )
            return (
              <div
                key={i}
                className={
                  styles.strengthBox + ' ' + (s.area ? styles[s.area] : '')
                }
              >
                <div>
                  <p className="font-semibold text-px-16 leading-px-24 md:text-px-24 md:leading-px-36">
                    {s.title}
                  </p>
                  <p className="text-px-13 leading-px-18 mt-px-4 md:mt-px-12 md:text-px-20 md:leading-px-30">
                    {s.text}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-px-11 leading-px-14_1 md:text-px-13 md:leading-px-18 ml:text-[10px] ml:leading-px-18 lg:text-px-13">
                    {s.smallText}
                  </p>
                  {s?.logo?.src && (
                    <Logo
                      alt={s.logo.alt}
                      className={s.logo.className}
                      src={s.logo.src}
                    />
                  )}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </>
  )
}
